import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Users, Mail, Loader2, Copy, CheckCircle, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Operator New Family Page - Invite-First Flow
 * Simple form: family_name + primary_parent_email
 * Creates family with status="pending_onboarding" and generates invite
 */
export const OperatorNewFamily = () => {
  const navigate = useNavigate();
  
  const [familyName, setFamilyName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    
    if (!familyName.trim()) {
      newErrors.familyName = 'Family name is required';
    }
    
    if (!parentEmail.trim()) {
      newErrors.parentEmail = 'Parent email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail)) {
      newErrors.parentEmail = 'Enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      // Step 1: Create family with pending status
      const familyResponse = await fetch(`${API}/families`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          family_name: familyName.trim(),
          parent_primary_email: parentEmail.trim().toLowerCase(),
          status: 'pending_onboarding'
        })
      });
      
      const familyData = await familyResponse.json();
      
      if (!familyResponse.ok || !familyData.id) {
        throw new Error(familyData.detail || 'Failed to create family');
      }
      
      const familyId = familyData.id;
      
      // Step 2: Create invite for this family
      const inviteResponse = await fetch(`${API}/operator/family/${familyId}/invite`, {
        method: 'POST'
      });
      
      const invite = await inviteResponse.json();
      
      if (!invite.ok) {
        throw new Error(invite.error || 'Failed to create invite');
      }
      
      // Build invite URL
      const inviteUrl = `${window.location.origin}/invite/${invite.token}`;
      
      setInviteData({
        familyId,
        familyName: familyName.trim(),
        inviteUrl,
        token: invite.token,
        expiresAt: invite.expires_at
      });
      
      toast.success('Family created! Share the invite link with the parent.');
      
    } catch (err) {
      console.error('Create family error:', err);
      toast.error(err.message || 'Failed to create family');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteData?.inviteUrl) return;
    
    try {
      await navigator.clipboard.writeText(inviteData.inviteUrl);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleViewFamily = () => {
    if (inviteData?.familyId) {
      navigate(`/operator/family/${inviteData.familyId}`);
    }
  };

  const handleCreateAnother = () => {
    setInviteData(null);
    setFamilyName('');
    setParentEmail('');
    setErrors({});
  };

  // Success state - show invite link
  if (inviteData) {
    return (
      <div className="max-w-lg mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-xl">Family Created!</CardTitle>
            <CardDescription>
              Share this invite link with <strong>{inviteData.familyName}</strong>
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Invite Link */}
            <div className="bg-slate-50 rounded-lg p-4">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Invite Link</Label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  readOnly
                  value={inviteData.inviteUrl}
                  className="flex-1 text-sm font-mono bg-white border border-slate-200 rounded px-3 py-2"
                  data-testid="invite-link-display"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className={copied ? 'bg-green-50 border-green-200' : ''}
                  data-testid="copy-invite-link"
                >
                  {copied ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Expires: {new Date(inviteData.expiresAt).toLocaleDateString()}
              </p>
            </div>

            {/* Status Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>Status:</strong> Pending Onboarding
              </p>
              <p className="text-xs text-amber-700 mt-1">
                The parent will complete onboarding after accepting the invite.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleViewFamily}
                data-testid="view-family-btn"
              >
                View Family
              </Button>
              <Button
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCreateAnother}
                data-testid="create-another-btn"
              >
                Create Another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form state
  return (
    <div className="max-w-lg mx-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-xl">New Family</CardTitle>
              <CardDescription>
                Create a family and generate an invite link
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Family Name */}
            <div className="space-y-2">
              <Label htmlFor="familyName">
                Family Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="familyName"
                type="text"
                placeholder="e.g., Sharma Family"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                data-testid="family-name-input"
                disabled={loading}
                className={errors.familyName ? 'border-red-500' : ''}
              />
              {errors.familyName && (
                <p className="text-sm text-red-500">{errors.familyName}</p>
              )}
            </div>

            {/* Parent Email */}
            <div className="space-y-2">
              <Label htmlFor="parentEmail" className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400" />
                Primary Parent Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="parentEmail"
                type="email"
                placeholder="parent@example.com"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                data-testid="parent-email-input"
                disabled={loading}
                className={errors.parentEmail ? 'border-red-500' : ''}
              />
              {errors.parentEmail && (
                <p className="text-sm text-red-500">{errors.parentEmail}</p>
              )}
              <p className="text-xs text-slate-500">
                The invite link will be sent to this email
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Invite-First Flow:</strong>
              </p>
              <ul className="text-xs text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li>Family will be created with "pending" status</li>
                <li>Parent completes onboarding via invite link</li>
                <li>Children, school, and nanny added during onboarding</li>
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={loading}
              data-testid="create-family-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Create Family & Generate Invite
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default OperatorNewFamily;
